import { type NavigateOptions, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { isEmpty, isEqual, isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { API } from '@shared/types/api';
import { MBSentry } from '@shared/util/sentry';

export const COLLECTION_CRITERIA_QUERY_PARAM = 'collectionCriteria';
export const CALENDAR_DATE_COMPONENT_ID_QUERY_PARAM = 'calendarDateComponentId';
export const VIEW_TEMPLATE_ID_QUERY_PARAM = 'viewTemplateId';

// CloudFront rejects requests whose URL (path + query string) exceeds 8192 bytes with a 414
// (SC-79196 follow-up). `collectionCriteria` is the only unbounded query param we write - a
// heavily filtered/grouped view (or a filter selecting many tag/related-card values) serializes to
// a large JSON blob - so we cap the total and refuse to write it past this budget rather than emit
// a URL that would 414. The margin below 8192 covers the origin path segment, the protocol/method
// on the request line, and any header accounting CloudFront folds in.
const MAX_QUERY_PARAM_URL_LENGTH = 7500;

// Fire the oversized-criteria telemetry at most once per session - the write is debounced but can
// still recur on every edit, and we only need to know that it's happening, not count keystrokes.
let hasWarnedOversizedCriteria = false;

// Plain (readable) JSON rather than a base64 blob, so a copied link can be inspected or
// hand-edited in the URL bar. `URLSearchParams`/the router handle percent-encoding automatically.
//
// Nil (`null`/`undefined`) fields are stripped before serializing. `getFilterComponent` builds each
// filter with every alternative `value.*` branch present but null (e.g. a tag filter still carries
// `numberIs: null`, `dateIs: null`, ...); those nulls are only meaningful as GraphQL input at
// save/query time, and repeated across many filters they bloat the URL enough to trip CloudFront's
// 8192-byte limit (→ 414). The decoder reads every field with `?.`/`isNil` (see
// `mapCollectionCriteriaToState`), so an omitted field is indistinguishable from an explicit null.
export function encodeCollectionCriteriaQueryParam(
  criteria: API.CollectionCriteriaFragment,
): string {
  return JSON.stringify(stripNilDeep(criteria));
}

// Recursively removes `null`/`undefined` object properties (preserving `false`/`0`/`''` and array
// contents). Used to keep the serialized `collectionCriteria` param as small as possible, and to
// compare two sets of filters by what they mean rather than by which null branches they spell out.
export function stripNilDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(stripNilDeep) as unknown as T;
  }
  if (typeof value === 'object' && !isNil(value)) {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      if (!isNil(val)) {
        result[key] = stripNilDeep(val);
      }
    });
    return result as T;
  }
  return value;
}

// The router's default search parser (see `useRawQueryParamValue`) eagerly runs `JSON.parse` on
// every string search value, so a `collectionCriteria` param written as plain JSON usually
// arrives here already parsed into an object rather than as a string. A raw/hand-edited string
// is supported too, as a fallback.
export function decodeCollectionCriteriaQueryParam(
  value: unknown,
): API.CollectionCriteriaFragment | null {
  if (isNil(value)) {
    return null;
  }
  const parsed = typeof value === 'string' ? tryJsonParse(value) : value;
  return isCollectionCriteria(parsed) ? parsed : null;
}

// Only checks for an object: the param is trusted to hold criteria this app wrote, and
// `mapCollectionCriteriaToState` reads every field defensively.
function isCollectionCriteria(value: unknown): value is API.CollectionCriteriaFragment {
  return typeof value === 'object' && !isNil(value);
}

/**
 * The `collectionCriteria` query param, captured once when this hook first mounts.
 * `PreserveCollectionCriteria` mirrors live filter/sort/group edits back into this same param
 * (see `useSyncQueryParams`) as the user edits, so re-reading it on every render would feed those
 * writes right back in as if a new link had been opened. Freezing the value at mount means a
 * link is only ever applied once, on initial load - consistent with a full page load/remount
 * being the only way a genuinely new link gets opened.
 */
export function useCollectionCriteriaQueryParamOverride(): API.CollectionCriteriaFragment | null {
  const rawValue = useRawQueryParamValue(COLLECTION_CRITERIA_QUERY_PARAM);
  const [initialOverride] = useState(() => decodeCollectionCriteriaQueryParam(rawValue));
  return initialOverride;
}

/**
 * The `calendarDateComponentId` query param, captured once when this hook first mounts - see
 * `useCollectionCriteriaQueryParamOverride` for why this is frozen rather than re-read live.
 */
export function useCalendarDateComponentIdQueryParamOverride(): string | null {
  const rawValue = useQueryParamValue(CALENDAR_DATE_COMPONENT_ID_QUERY_PARAM);
  const [initialValue] = useState(() => rawValue);
  return isEmpty(initialValue) ? null : initialValue;
}

/**
 * The `viewTemplateId` query param, identifying which view template a shared/bookmarked
 * collection view link should open to (see `useCollectionViewTemplate`). Unlike the overrides
 * above, this is read live rather than frozen at mount: it's only ever consulted as a fallback
 * when the URL has no `templateSlug` path segment, and any explicit navigation to a different
 * view template always sets that path segment, which then takes precedence - so once real
 * navigation happens, this override naturally stops applying with no freezing required.
 */
export function useViewTemplateIdQueryParamOverride(): string | null {
  const rawValue = useQueryParamValue(VIEW_TEMPLATE_ID_QUERY_PARAM);
  return isEmpty(rawValue) ? null : rawValue;
}

/**
 * Top-level `ViewTemplate` fields that a link is allowed to override via query param. Each
 * field's query param name matches the field name (see `getViewTemplateQueryParamSyncUpdates`),
 * so a new override can be added here (and to `OVERRIDABLE_VIEW_TEMPLATE_FIELDS` below) with no
 * further per-field wiring required - callers like `ViewTemplateProvider`/`PreserveViewTemplate`
 * apply whatever comes back generically, with no per-field wiring required.
 */
export type ViewTemplateQueryParamOverrides = Partial<
  Pick<API.ViewTemplateFragment, 'calendarDateComponentId'>
>;

const OVERRIDABLE_VIEW_TEMPLATE_FIELDS: (keyof ViewTemplateQueryParamOverrides)[] = [
  'calendarDateComponentId',
];

export function useViewTemplateQueryParamOverrides(
  viewTemplate: Pick<API.ViewTemplateFragment, 'subtype'>,
): ViewTemplateQueryParamOverrides {
  const calendarDateComponentId = useCalendarDateComponentIdQueryParamOverride();
  return useMemo((): ViewTemplateQueryParamOverrides => {
    // calendarDateComponentId is only applied when the currently viewed view template is already
    // a CALENDAR view - this lets a link override which date field a calendar view is grouped by,
    // without forcing a subtype switch.
    if (viewTemplate.subtype === 'CALENDAR' && !isNil(calendarDateComponentId)) {
      return { calendarDateComponentId };
    }
    return {};
  }, [viewTemplate.subtype, calendarDateComponentId]);
}

/**
 * Builds the full set of overridable view template query params for the given view template,
 * with `null` for any field that doesn't currently apply (e.g. calendarDateComponentId when the
 * subtype isn't CALENDAR). Passing the whole set to `useSyncQueryParams` - rather than only the
 * currently-applicable fields - ensures a param left over from a field that stopped applying
 * mid-session (e.g. the subtype changed) gets cleared out too.
 */
export function getViewTemplateQueryParamSyncUpdates(
  viewTemplate: Pick<API.ViewTemplateFragment, 'subtype' | 'calendarDateComponentId'>,
): Record<string, unknown> {
  // Mirrors the applicability rule in `useViewTemplateQueryParamOverrides` - calendarDateComponentId
  // only applies to CALENDAR views.
  const applicableOverrides: ViewTemplateQueryParamOverrides =
    viewTemplate.subtype === 'CALENDAR'
      ? { calendarDateComponentId: viewTemplate.calendarDateComponentId }
      : {};
  return OVERRIDABLE_VIEW_TEMPLATE_FIELDS.reduce(
    (memo, field) => ({ ...memo, [field]: applicableOverrides[field] ?? null }),
    {} as Record<string, unknown>,
  );
}

/** Clears every overridable view template query param from the URL. */
export function clearViewTemplateQueryParamOverrides(): Record<string, null> {
  return OVERRIDABLE_VIEW_TEMPLATE_FIELDS.reduce(
    (memo, field) => ({ ...memo, [field]: null }),
    {} as Record<string, null>,
  );
}

/**
 * Merges the given search param updates into the URL (replacing the current history entry
 * rather than pushing a new one). Pass `null`/`undefined` for a param to remove it. Used to keep
 * the address bar mirroring live ephemeral UI edits (collectionCriteria, view template field
 * overrides) as the user makes them, so a copied/bookmarked link always reflects what's on
 * screen - see `PreserveCollectionCriteria`/`PreserveViewTemplate`.
 *
 * Skips `navigate` entirely when the updates wouldn't change the current search. Callers invoke
 * this unconditionally on every render where a param *might* be stale (e.g. clearing a param
 * whenever a route isn't the one that owns it), so the common case is a no-op.
 *
 * Writes target the *absolute* current pathname rather than the relative `.`. This is load-bearing
 * (SC-79196): `navigate({ to: '.' })` resolves `.` against `router.pendingBuiltLocation` when a
 * navigation is in flight (see router-core `buildLocation`). If the user edits a filter (kicking
 * off a debounced param write whose `pendingBuiltLocation` is still the current channel) and then
 * switches channels before it settles, a subsequent `.` write resolves back to the channel being
 * left and `replace`-navigates the user right back to it - making it impossible to leave a channel
 * while a filter is active. An absolute `to` ignores the resolution base entirely (see router-core
 * `resolvePath`), so a param write can never redirect the user.
 *
 * Writes are also capped at `MAX_QUERY_PARAM_URL_LENGTH`: `collectionCriteria` can serialize large
 * enough that the URL trips CloudFront's 8192-byte limit and returns a 414. When the write would
 * exceed the budget we drop `collectionCriteria` (and the `viewTemplateId` that only exists to pin
 * it) rather than emit an un-loadable URL - the edits still live in localStorage and still apply
 * locally, they just aren't reflected in a shareable link.
 */
export function useSyncQueryParams(): (updates: Record<string, unknown>) => void {
  const router = useRouter();
  const navigate = useNavigate();
  return useCallback(
    (updates: Record<string, unknown>) => {
      // Read the freshest location at call time (not via a closed-over/memoized value) so the
      // no-op check and the write target always reflect where the user actually is right now.
      const { pathname, search: currentSearch } = router.latestLocation;
      const target = resolveSyncedSearch(pathname, currentSearch, updates);
      // `null` means either nothing changed (skip the no-op navigate) or the write was refused for
      // being un-representably large - either way there is nothing to write.
      if (isNil(target)) {
        return;
      }
      navigate({
        // Absolute path (already starts with `/`) - never `.` - so resolution can't be poisoned by
        // an in-flight navigation's `pendingBuiltLocation` and bounce the user (see the JSDoc above).
        to: pathname,
        search: () => target,
        replace: true,
        // `to` is a runtime pathname string rather than a statically-known route id, which the
        // typed router API can't express; the value is always a valid absolute path from the router.
      } as NavigateOptions);
    },
    [navigate, router],
  );
}

/**
 * Pure resolution of what the search params should become after applying `updates` to
 * `currentSearch`, with the CloudFront URL-length guard applied. Returns the next search object, or
 * `null` when there is nothing to write (an unchanged result, or a write refused for exceeding the
 * URL budget even after dropping `collectionCriteria`). Extracted from `useSyncQueryParams` so the
 * size-guard behavior can be unit-tested without a router.
 */
export function resolveSyncedSearch(
  pathname: string,
  currentSearch: Record<string, unknown>,
  updates: Record<string, unknown>,
): Record<string, unknown> | null {
  let target = applyQueryParamUpdates(currentSearch, updates);

  // Size guard: never emit a URL long enough to trip CloudFront's 8KB limit (→ 414).
  if (estimateQueryParamUrlLength(pathname, target) > MAX_QUERY_PARAM_URL_LENGTH) {
    // `collectionCriteria` is the only unbounded param; drop it and the `viewTemplateId` that
    // exists solely to pin it, so the URL stays loadable. The filter is unaffected locally - it is
    // still persisted to localStorage and still applied on screen.
    target = applyQueryParamUpdates(target, {
      [COLLECTION_CRITERIA_QUERY_PARAM]: null,
      [VIEW_TEMPLATE_ID_QUERY_PARAM]: null,
    });
    if (!hasWarnedOversizedCriteria) {
      hasWarnedOversizedCriteria = true;
      MBSentry.exception({
        error: new Error('collectionCriteria query param dropped: exceeds URL length budget'),
        data: { pathname, maxLength: MAX_QUERY_PARAM_URL_LENGTH },
      });
    }
    if (estimateQueryParamUrlLength(pathname, target) > MAX_QUERY_PARAM_URL_LENGTH) {
      // Some other param is oversized (not expected) - refuse the write rather than risk a 414.
      return null;
    }
  }

  // Nothing actually changed - callers (e.g. `PreserveCollectionCriteria`) fire these on every
  // relevant render, so the vast majority of calls are no-ops.
  if (isEqual(target, currentSearch)) {
    return null;
  }
  return target;
}

/** Applies a set of search-param updates (a `null`/`undefined` value removes the param). */
function applyQueryParamUpdates(
  search: Record<string, unknown>,
  updates: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...search };
  Object.entries(updates).forEach(([key, value]) => {
    if (isNil(value)) {
      delete next[key];
    } else {
      next[key] = value;
    }
  });
  return next;
}

/**
 * Conservatively estimates the on-wire length of `pathname?search`, matching the router's default
 * search serializer (each value `JSON.stringify`d then percent-encoded). Used only to keep the URL
 * under CloudFront's limit, so over-estimating is safe - it just drops the param slightly sooner.
 */
function estimateQueryParamUrlLength(pathname: string, search: Record<string, unknown>): number {
  const query = Object.entries(search)
    .filter(([, value]) => !isNil(value))
    .map(
      ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`,
    )
    .join('&');
  return pathname.length + (query.length > 0 ? query.length + 1 : 0);
}

// Route-agnostic search read: this override can apply on any collection view route, and
// TanStack Router's strict typing only covers params declared via `validateSearch`.
function useRawQueryParamValue(paramName: string): unknown {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return search[paramName];
}

// String-valued params (calendarDateComponentId) are simple words that the router's
// `JSON.parse`-based auto-parsing can't interpret as JSON, so they normally arrive as plain
// strings; this coerces other primitives just in case and rejects unexpected objects.
function useQueryParamValue(paramName: string): string {
  const rawValue = useRawQueryParamValue(paramName);
  if (isNil(rawValue)) {
    return '';
  }
  if (typeof rawValue === 'string') {
    return rawValue;
  }
  if (typeof rawValue === 'object') {
    return '';
  }
  return String(rawValue);
}

function tryJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
